import { NetworkStatus } from '@apollo/client';
import { gql, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';

import { CLIENT_QUERY_FIELDS } from 'src/graphql/entities/client/client.fragments';
import { mapClients } from 'src/graphql/entities/client/client.mapping';
import { DEFAULT_CLIENTS_QUERY_LIMIT } from 'src/graphql/entities/client/client.operations';
import {
  buildDynamicAttributeVariables,
  ProjectedDynamicAttribute,
} from 'src/graphql/entities/custom-attributes/dynamic-attributes-projection';
import { ConditionInput, QueryVariables, Records, SortingInput } from 'src/graphql/types';
import { useLoadMore } from 'src/graphql/utils';
import { ClientRaw } from 'src/types';
import { useCacheManager } from 'src/utils/hooks/use-cache-manager';
import useDebouncedValue from 'src/utils/hooks/use-debounced-value';

/**
 * Response shape of the clients list query. Shared by the search readers
 * (`useSearchClients`, `use-selected-record`) and `use-requester-getters`.
 */
export type GetClientsQuery = {
  getClient: Records<ClientRaw & { dynamicAttributes?: ProjectedDynamicAttribute[] }>;
};

/**
 * The clients list/search query. Owned by the search feature — the only place
 * that fetches lists/batches of clients. Views that render a single client read
 * the normalized cache via `useFragment` instead (see `pages/Clients`).
 */
export const SEARCH_CLIENTS_QUERY = gql`
  query GetClient(
    $params: QueryParamsWithSearch
    $skipTotalRecords: Boolean = false
    $hasCustomAttributes: Boolean = false
  ) @entity(name: "Client") {
    getClient(queryParams: $params) {
      ...ClientQueryFields
    }
  }
  ${CLIENT_QUERY_FIELDS}
`;

const DEFAULT_FILTER: ConditionInput[] = [];

const DEFAULT_TARGET_IDS = [
  'client/clientId',
  'clientPhoneNumber/client/clientId',
  'clientEmail/client/clientId',
  'address/clients/clientId',
];

export const useSearchClients = (params: {
  searchTerm: string | null;
  targetIdAttributes?: string[];
  filter?: ConditionInput[];
  partnerId?: number | null;
  limit?: number;
  sort?: SortingInput | SortingInput[];
  skip?: boolean;
  showResultsWhenEmptySearchTerm?: boolean;
  dynamicAttributePaths?: string[];
}) => {
  const searchTerm = useDebouncedValue(params.searchTerm, 500);

  const {
    filter = DEFAULT_FILTER,
    skip,
    sort,
    showResultsWhenEmptySearchTerm,
    targetIdAttributes,
    partnerId,
    limit,
    dynamicAttributePaths,
  } = params;

  const queryOptions = useMemo(() => {
    const searchParams = searchTerm
      ? {
          searchTerm,
          targetIdAttributes: targetIdAttributes ?? DEFAULT_TARGET_IDS,
        }
      : undefined;

    const partnerScopedFilters =
      partnerId === null || partnerId === undefined
        ? filter
        : [...filter, { attribute: 'partnerId', operator: '=', value: String(partnerId) }];

    return {
      skip: skip || (!showResultsWhenEmptySearchTerm && !searchTerm),
      notifyOnNetworkStatusChange: true,
      variables: {
        skipTotalRecords: !!searchParams,
        ...buildDynamicAttributeVariables(dynamicAttributePaths),
        params: {
          searchParams,
          filters: partnerScopedFilters,
          sorting: sort,
          limit: limit ?? DEFAULT_CLIENTS_QUERY_LIMIT,
        },
      },
    };
  }, [
    skip,
    showResultsWhenEmptySearchTerm,
    searchTerm,
    targetIdAttributes,
    partnerId,
    filter,
    sort,
    limit,
    dynamicAttributePaths,
  ]);

  const useQueryResult = useQuery<GetClientsQuery, QueryVariables>(
    SEARCH_CLIENTS_QUERY,
    queryOptions,
  );

  const {
    data: rawData,
    fetchMore,
    previousData,
    client,
    variables,
    loading,
    networkStatus,
    ...rest
  } = useQueryResult;

  const cacheManager = useCacheManager<GetClientsQuery, QueryVariables>(
    SEARCH_CLIENTS_QUERY,
    useQueryResult,
  );

  const data = rawData || previousData;

  const [clients, rawClientsMap] = useMemo(
    () => [
      mapClients(data?.getClient?.records),
      new Map(data?.getClient?.records.map((i) => [i.id, i])),
    ],
    [data?.getClient?.records],
  );

  const { loadMore, hasReachedEnd } = useLoadMore<GetClientsQuery>({
    offset: data?.getClient?.records?.length ?? 0,
    fetchMore,
    params: variables?.params!,
  });

  const totalRecords = data?.getClient.metadata?.totalRecords ?? 0;

  return {
    ...rest,
    isLoading: loading,
    isLoadingMore: networkStatus === NetworkStatus.fetchMore,
    records: clients,
    rawClientsMap,
    loadMore,
    totalRecords,
    hasMore: data?.getClient.metadata?.totalRecords
      ? totalRecords > (data?.getClient?.records?.length ?? 0)
      : !hasReachedEnd,
    cacheManager,
  };
};
